import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { Category, PerfexLead } from '../types';

interface PerfexImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function PerfexImportModal({ isOpen, onClose, onSuccess }: PerfexImportModalProps) {
  const [leads, setLeads] = useState<PerfexLead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<PerfexLead[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Filtros
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    company: '',
    status: '',
    source: '',
    country: '',
    city: '',
    state: ''
  });

  // Valores únicos para filtros select
  const [uniqueValues, setUniqueValues] = useState({
    statuses: [] as string[],
    sources: [] as string[],
    countries: [] as string[],
    cities: [] as string[],
    states: [] as string[]
  });

  const loadCategories = async () => {
    try {
      const response = await apiService.getCategories();
      const categoriesData = response.categories || [];
      setCategories(categoriesData);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias');
      setCategories([]);
    }
  };

  const loadLeads = async () => {
    setIsLoadingLeads(true);
    try {
      const response = await apiService.getPerfexLeads();
      const leadsData = response.leads || [];
      setLeads(leadsData);
      setFilteredLeads(leadsData);

      // Extrair valores únicos para os filtros (usando nomes legíveis quando disponíveis)
      const statuses = [...new Set(leadsData.map((l: PerfexLead) => l.status_name || l.status).filter(Boolean))];
      const sources = [...new Set(leadsData.map((l: PerfexLead) => l.source_name || l.source).filter(Boolean))];
      const countries = [...new Set(leadsData.map((l: PerfexLead) => l.country).filter(Boolean))];
      const cities = [...new Set(leadsData.map((l: PerfexLead) => l.city).filter(Boolean))];
      const states = [...new Set(leadsData.map((l: PerfexLead) => l.state).filter(Boolean))];

      setUniqueValues({ statuses, sources, countries, cities, states });

      if (leadsData.length === 0) {
        toast('Nenhum lead encontrado no Perfex CRM', { icon: 'ℹ️' });
      } else {
        toast.success(`${leadsData.length} leads carregados do Perfex CRM`);
      }
    } catch (error: unknown) {
      console.error('Erro ao carregar leads do Perfex:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao carregar leads do Perfex CRM';

      if (errorMessage.includes('não configurado')) {
        toast.error('Configure o Perfex CRM na página de Integrações primeiro', { duration: 5000 });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoadingLeads(false);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...leads];

    if (filters.name) {
      filtered = filtered.filter(lead =>
        lead.name?.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    if (filters.email) {
      filtered = filtered.filter(lead =>
        lead.email?.toLowerCase().includes(filters.email.toLowerCase())
      );
    }

    if (filters.company) {
      filtered = filtered.filter(lead =>
        lead.company?.toLowerCase().includes(filters.company.toLowerCase())
      );
    }

    if (filters.status) {
      filtered = filtered.filter(lead =>
        (lead.status_name || lead.status) === filters.status
      );
    }

    if (filters.source) {
      filtered = filtered.filter(lead =>
        (lead.source_name || lead.source) === filters.source
      );
    }

    if (filters.country) {
      filtered = filtered.filter(lead => lead.country === filters.country);
    }

    if (filters.city) {
      filtered = filtered.filter(lead => lead.city === filters.city);
    }

    if (filters.state) {
      filtered = filtered.filter(lead => lead.state === filters.state);
    }

    setFilteredLeads(filtered);
  }, [filters, leads]);

  const clearFilters = () => {
    setFilters({
      name: '',
      email: '',
      company: '',
      status: '',
      source: '',
      country: '',
      city: '',
      state: ''
    });
  };

  const toggleLeadSelection = (leadId: string) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeads(newSelected);
  };

  const selectAllFiltered = () => {
    const newSelected = new Set<string>();
    filteredLeads.forEach(lead => newSelected.add(lead.id));
    setSelectedLeads(newSelected);
  };

  const deselectAll = () => {
    setSelectedLeads(new Set());
  };

  const handleImport = async () => {
    if (selectedLeads.size === 0) {
      toast.error('Selecione pelo menos um lead para importar');
      return;
    }

    if (!selectedCategoryId) {
      toast.error('Selecione uma categoria para os contatos');
      return;
    }

    setIsImporting(true);
    try {
      const response = await apiService.importPerfexLeads(
        Array.from(selectedLeads),
        selectedCategoryId
      );

      toast.success(
        `Importação concluída! ${response.imported} contatos importados, ${response.updated} atualizados`
      );

      onSuccess();
      handleClose();
    } catch (error: unknown) {
      console.error('Erro ao importar:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao importar contatos';
      toast.error(errorMessage);
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setLeads([]);
    setFilteredLeads([]);
    setSelectedLeads(new Set());
    setSelectedCategoryId('');
    clearFilters();
    onClose();
  };

  // Effects
  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#0f172a]/80 flex items-center justify-center p-4 z-50 backdrop-blur-md overflow-y-auto">
      <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-[2.5rem] shadow-2xl w-full max-w-6xl border border-white/10 my-8 max-h-[90vh] flex flex-col overflow-hidden backdrop-blur-md">
        {/* Header */}
        <div className="bg-white/5 p-10 border-b border-white/10 backdrop-blur-md">
          <div className="flex justify-between items-start mb-8">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600/20 to-blue-900/20 rounded-2xl flex items-center justify-center border border-blue-500/20 shadow-inner group">
                <svg className="w-8 h-8 text-blue-400 group-hover:scale-110 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold font-oswald text-white uppercase tracking-tight">IMPORTAR LEADS DO PERFEX CRM</h2>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald mt-2">Carregue e selecione leads para importar como contatos</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-4 text-gray-400 hover:text-white hover:bg-white/10 rounded-2xl transition-all border border-white/5 hover:border-white/10"
              aria-label="Fechar"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Botão Carregar Leads */}
          <div className="flex justify-between items-center gap-4">
            <button
              onClick={loadLeads}
              disabled={isLoadingLeads}
              className="px-8 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 border border-blue-500 relative overflow-hidden group/btn font-oswald flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
              {isLoadingLeads ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Carregando...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {leads.length > 0 ? 'Atualizar Leads' : 'Carregar Leads'}
                </>
              )}
            </button>

            {leads.length > 0 && (
              <div className="px-6 py-3 bg-white/5 rounded-2xl border border-white/10 flex items-center gap-4">
                <div className="flex flex-col items-end">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald">Total Leads</span>
                  <span className="text-white font-bold font-oswald">{filteredLeads.length} de {leads.length}</span>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div className="flex flex-col items-end">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald">Selecionados</span>
                  <span className="text-blue-400 font-bold font-oswald">{selectedLeads.size}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filtros */}
        {leads.length > 0 && (
          <div className="p-10 border-b border-white/10 bg-white/5">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">FILTROS DE BUSCA</h3>
              <button
                onClick={clearFilters}
                className="text-[10px] font-bold text-blue-400 hover:text-blue-300 uppercase tracking-[0.2em] font-oswald transition-colors"
              >
                Limpar Filtros
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-2">Nome</label>
                <input
                  type="text"
                  placeholder="Buscar por nome..."
                  value={filters.name}
                  onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder-gray-500 transition-all font-oswald"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-2">Email</label>
                <input
                  type="text"
                  placeholder="Buscar por email..."
                  value={filters.email}
                  onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder-gray-500 transition-all font-oswald"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-2">Empresa</label>
                <input
                  type="text"
                  placeholder="Buscar por empresa..."
                  value={filters.company}
                  onChange={(e) => setFilters({ ...filters, company: e.target.value })}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder-gray-500 transition-all font-oswald"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all font-oswald appearance-none"
                >
                  <option value="" className="bg-[#1e293b]">Todos os Status</option>
                  {uniqueValues.statuses.map(status => (
                    <option key={status} value={status} className="bg-[#1e293b]">{status}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-2">Fonte</label>
                <select
                  value={filters.source}
                  onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all font-oswald appearance-none"
                >
                  <option value="" className="bg-[#1e293b]">Todas as Fontes</option>
                  {uniqueValues.sources.map(source => (
                    <option key={source} value={source} className="bg-[#1e293b]">{source}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-2">País</label>
                <select
                  value={filters.country}
                  onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all font-oswald appearance-none"
                >
                  <option value="" className="bg-[#1e293b]">Todos os Países</option>
                  {uniqueValues.countries.map(country => (
                    <option key={country} value={country} className="bg-[#1e293b]">{country}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-2">Cidade</label>
                <select
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all font-oswald appearance-none"
                >
                  <option value="" className="bg-[#1e293b]">Todas as Cidades</option>
                  {uniqueValues.cities.map(city => (
                    <option key={city} value={city} className="bg-[#1e293b]">{city}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-2">Estado</label>
                <select
                  value={filters.state}
                  onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all font-oswald appearance-none"
                >
                  <option value="" className="bg-[#1e293b]">Todos os Estados</option>
                  {uniqueValues.states.map(state => (
                    <option key={state} value={state} className="bg-[#1e293b]">{state}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Leads */}
        <div className="flex-1 overflow-y-auto p-10 custom-scrollbar">
          {leads.length === 0 && !isLoadingLeads && (
            <div className="bg-blue-600/5 border border-blue-500/20 rounded-[2rem] p-10 backdrop-blur-md">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center border border-blue-500/20">
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white font-oswald uppercase tracking-tight">Como funciona a importação</h3>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { icon: "🔄", text: "Clique em \"Carregar Leads\" para sincronizar com o Perfex CRM" },
                  { icon: "🔍", text: "Use os filtros inteligentes para refinar sua busca" },
                  { icon: "✅", text: "Selecione individualmente ou em massa os leads desejados" },
                  { icon: "📁", text: "Defina uma categoria e importe para sua lista de contatos" }
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-4 p-4 bg-white/5 rounded-2xl border border-white/5 hover:border-white/10 transition-all">
                    <span className="text-2xl">{item.icon}</span>
                    <span className="text-gray-400 text-sm font-medium leading-relaxed">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {filteredLeads.length > 0 && (
            <>
              <div className="flex gap-4 mb-8">
                <button
                  onClick={selectAllFiltered}
                  className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl transition-all border border-white/5 hover:border-white/10 font-oswald"
                >
                  Selecionar Todos ({filteredLeads.length})
                </button>
                <button
                  onClick={deselectAll}
                  className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl transition-all border border-white/5 hover:border-white/10 font-oswald"
                >
                  Desmarcar Todos
                </button>
              </div>

              <div className="rounded-[2rem] overflow-hidden border border-white/10 bg-white/5 backdrop-blur-md">
                <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-[#0f172a]/80 sticky top-0 z-10 backdrop-blur-xl border-b border-white/10">
                      <tr>
                        <th className="px-8 py-6 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
                          <input type="checkbox" className="opacity-0" />
                        </th>
                        <th className="px-8 py-6 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Nome</th>
                        <th className="px-8 py-6 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Email</th>
                        <th className="px-8 py-6 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Telefone</th>
                        <th className="px-8 py-6 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Empresa</th>
                        <th className="px-8 py-6 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Status</th>
                        <th className="px-8 py-6 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Fonte</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {filteredLeads.map((lead) => (
                        <tr
                          key={lead.id}
                          className={`group hover:bg-white/5 transition-all duration-300 ${selectedLeads.has(lead.id) ? 'bg-blue-600/10' : ''}`}
                        >
                          <td className="px-8 py-6">
                            <input
                              type="checkbox"
                              checked={selectedLeads.has(lead.id)}
                              onChange={() => toggleLeadSelection(lead.id)}
                              className="w-5 h-5 rounded-lg border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500/50 cursor-pointer"
                            />
                          </td>
                          <td className="px-8 py-6">
                            <div className="font-bold text-white font-oswald uppercase tracking-wide group-hover:text-blue-400 transition-colors">
                              {lead.name || '-'}
                            </div>
                          </td>
                          <td className="px-8 py-6 text-gray-400 text-sm">{lead.email || '-'}</td>
                          <td className="px-8 py-6 text-gray-400 text-sm">{lead.phonenumber || '-'}</td>
                          <td className="px-8 py-6 text-gray-400 text-sm">{lead.company || '-'}</td>
                          <td className="px-8 py-6">
                            {(lead.status_name || lead.status) && (
                              <span className="px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-[0.1em] font-oswald bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                {lead.status_name || lead.status}
                              </span>
                            )}
                          </td>
                          <td className="px-8 py-6 text-gray-400 text-sm font-oswald uppercase tracking-widest text-[10px]">
                            {lead.source_name || lead.source || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer - Seleção de Categoria e Ações */}
        {leads.length > 0 && (
          <div className="p-10 border-t border-white/10 bg-white/5 backdrop-blur-md">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex items-center gap-6 w-full md:w-auto">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald whitespace-nowrap">
                  CATEGORIA DE DESTINO:
                </label>
                <div className="relative w-full md:w-80">
                  <select
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none font-oswald transition-all"
                  >
                    <option value="" className="bg-[#1e293b]">Selecione uma categoria...</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id} className="bg-[#1e293b]">
                        {cat.nome}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 w-full md:w-auto">
                <button
                  onClick={handleClose}
                  className="flex-1 md:flex-none px-8 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl hover:bg-white/10 transition-all border border-white/5 hover:border-white/10 font-oswald"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleImport}
                  disabled={isImporting || selectedLeads.size === 0 || !selectedCategoryId}
                  className="flex-1 md:flex-none px-12 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 border border-blue-500 relative overflow-hidden group/btn font-oswald disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  {isImporting ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Importando...
                    </div>
                  ) : (
                    `Importar ${selectedLeads.size} Contatos`
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
